import type { TFunction } from 'i18next';
import { IconButton, Edit } from 'qampo-ui-library';
import TrashIcon from '../../assets/icons/trash.svg?react';
import type { UserDetailResponse } from '../../api/generated';
import type { TableColumn } from '../UI';
import { Tooltip } from '../UI';
import { formatUserRoles } from '../../services/userRoleLabelService';
import styles from './UsersSettings.module.css';

interface UserColumnCallbacks {
  // The signed-in user's id, so their own delete button can be locked. Null
  // while there is no token yet: nothing is locked then, and the backend still
  // refuses the delete.
  currentUserId: string | null;
  onEdit: (user: UserDetailResponse) => void;
  onDelete: (user: UserDetailResponse) => void;
}

export const getUserColumns = (
  t: TFunction,
  { currentUserId, onEdit, onDelete }: UserColumnCallbacks
): TableColumn<UserDetailResponse>[] => [
  {
    key: 'name',
    header: t('config-user-col-name'),
    render: (user) => (
      <span className={styles.nameCell}>{`${user.firstName} ${user.lastName}`.trim()}</span>
    )
  },
  {
    key: 'email',
    header: t('config-user-col-email'),
    render: (user) => <span className={styles.emailCell}>{user.email}</span>
  },
  {
    key: 'roles',
    header: t('config-user-col-roles'),
    render: (user) => <span className={styles.rolesCell}>{formatUserRoles(t, user)}</span>
  },
  {
    key: 'accountAdmin',
    header: t('config-user-col-admin'),
    width: 150,
    headerAlign: 'center',
    cellStyle: { justifyContent: 'center' },
    // Its own column rather than part of the roles cell: administering the
    // account is a flag on the user, not a role held at a centre.
    render: (user) =>
      user.accountAdmin ? (
        <span className={`${styles.badge} ${styles.badgeOn}`}>{t('config-user-admin-yes')}</span>
      ) : (
        <span className={`${styles.badge} ${styles.badgeOff}`}>{t('config-user-admin-no')}</span>
      )
  },
  {
    key: 'actions',
    header: '',
    width: 100,
    headerAlign: 'center',
    render: (user) => {
      // Deleting yourself is refused by the backend, so the button is locked
      // rather than left to fail: it kept the confirmation dialog opening on a
      // delete that could never go through, then stacking an error dialog on
      // top of it.
      const isSelf = currentUserId !== null && user.userId === currentUserId;
      const deleteButton = (
        <IconButton
          size="small"
          icon={<TrashIcon width={14} height={14} />}
          onClick={() => onDelete(user)}
          disable={isSelf}
          classes={`${styles.actionBtn} ${styles.actionBtnDelete}`}
        />
      );

      return (
        <div className={styles.rowActions}>
          <IconButton
            size="small"
            icon={<Edit width={14} height={14} />}
            onClick={() => onEdit(user)}
            classes={styles.actionBtn}
          />
          {isSelf ? (
            <Tooltip
              trigger={deleteButton}
              triggerClassName={styles.tooltipTrigger}
              content={
                <span className={styles.tooltipContent}>{t('config-user-delete-self')}</span>
              }
            />
          ) : (
            deleteButton
          )}
        </div>
      );
    }
  }
];
